import { ApplyFunction } from "../ApplyFunction";
import { SafeObjectErrorLog } from "../CurrentPhase";
import { ICmd, ICmdUtils } from "../cmds/ICmd";
import { Process, ProcessUtils } from "../Process";

export interface DetailedSafeObject extends ICmd {
  readonly isValid: boolean;
  readonly process: Process;
  readonly nestedObjects: DetailedSafeObject[];
  readonly isDeleted: boolean;
  readonly applyFunction: ApplyFunction | null;
  readonly source: string;
  readonly destination: string;
  readonly timestamp: number;
  readonly last_modified: number;
  readonly slot: number;
  readonly pending_objects: DetailedSafeObject[];
  readonly affected_by: DetailedSafeObject[];
  safes?: Array<Record<string, any>> | null;
  trustors?: Array<Record<string, any>> | null;
  public_key?: string | null;
}

// Utility functions for IDetailedSafeObject
export const DetailedSafeObjectUtils = {
  // Create a new IDetailedSafeObject
  create({
    id,
    cls,
    isValid,
    process,
    nestedObjects,
    isDeleted,
    applyFunction,
    originalObjectSIG,
    source,
    destination,
    timestamp,
    last_modified,
    slot,
    pending_objects,
    affected_by,
    parentGroup,
    SIGNATURE = "",
    comment = "",
    safes,
    trustors,
    public_key,
    execute,
  }: {
    isValid: boolean;
    process: Process;
    nestedObjects: DetailedSafeObject[];
    isDeleted: boolean;
    applyFunction: ApplyFunction | null;
    originalObjectSIG: string;
    source: string;
    destination: string;
    timestamp: number;
    last_modified: number;
    slot: number;
    pending_objects: DetailedSafeObject[];
    affected_by: DetailedSafeObject[];
    cls: string;
    id: string;
    parentGroup: string;
    SIGNATURE?: string;
    comment?: string;
    safes?: Array<Record<string, any>> | null;
    trustors?: Array<Record<string, any>> | null;
    public_key?: string | null;
    execute: boolean;
  }): DetailedSafeObject {
    return {
      cls,
      id,
      parentGroup,
      SIGNATURE,
      originalObjectSIG,
      comment,
      applyFunction,
      execute,
      isValid,
      process,
      nestedObjects,
      isDeleted,
      source,
      destination,
      timestamp,
      last_modified,
      slot,
      pending_objects,
      affected_by,
      safes,
      trustors,
      public_key,
    };
  },

  // Create empty IDetailedSafeObject
  empty(): DetailedSafeObject {
    const tms = Date.now();
    return {
      cls: "",
      id: "",
      parentGroup: "",
      SIGNATURE: "",
      originalObjectSIG: "",
      comment: "",
      applyFunction: null,
      execute: true,
      isValid: false,
      process: ProcessUtils.empty(),
      nestedObjects: [],
      isDeleted: false,
      source: "",
      destination: "",
      timestamp: tms,
      last_modified: tms,
      slot: -1,
      pending_objects: [],
      affected_by: [],
    };
  },

  // Check if no active transaction rejected
  isNoActiveTxRejected(obj: DetailedSafeObject, errors: SafeObjectErrorLog[]): boolean {
    if (errors.length !== 1) return true;
    if (errors[0].conditionClass !== "condition.noactive") return true;
    return false;
  },

  // Convert to map
  toMap(obj: DetailedSafeObject): Record<string, any> {
    const superMap = ICmdUtils.toMap(obj);
    const object: Record<string, any> = {
      //   id: obj.id,
      //   parentGroup: obj.parentGroup,
      //   comment: obj.comment,
      //   SIGNATURE: obj.SIGNATURE,
      source: obj.source,
      destination: obj.destination,
      safes: obj.safes,
      trustors: obj.trustors,
      slot: obj.slot === -1,
      timestamp: obj.timestamp === -1 ? Date.now() : obj.timestamp,
      last_modified: obj.last_modified === -1 ? Date.now() : obj.last_modified,
      pending_objects:
        obj.pending_objects.length > 0
          ? obj.pending_objects.map((po) => DetailedSafeObjectUtils.toMap(po))
          : [],
    };
    // if (obj.originalObjectSIG !== "") object["originalObjectSIG"] = obj.originalObjectSIG;
    // if (obj.applyFunction && obj.applyFunction.functionName !== "") object["applyFunction"] = ApplyFunctionUtils.toMap(obj.applyFunction);

    return {
      ...superMap,
      object,
      isValid: obj.isValid,
      isDeleted: obj.isDeleted,
      nestedObjects: obj.nestedObjects,
      process: ProcessUtils.toMap(obj.process),
    };
  },

  // Convert to JSON
  toJson(obj: DetailedSafeObject): string {
    return JSON.stringify(DetailedSafeObjectUtils.toMap(obj));
  },

  // Convert to string
  toString(obj: DetailedSafeObject): string {
    return `{id: ${obj.id}, class: ${obj.cls}, parentGroup: ${obj.parentGroup}, SIGNATURE: ${obj.SIGNATURE}, comment: ${obj.comment}, isValid: ${obj.isValid}, process: ${obj.process}, nestedObjects: ${JSON.stringify(obj.nestedObjects)}, isDeleted: ${obj.isDeleted}${obj.applyFunction ? `, applyFunction: ${obj.applyFunction}` : ""}${obj.originalObjectSIG ? `, originalObjectSIG: ${obj.originalObjectSIG}` : ""}, source: ${obj.source}, destination: ${obj.destination}, timestamp: ${obj.timestamp}, slot: ${obj.slot}, pending_objects: ${JSON.stringify(obj.pending_objects)}}`;
  },

  // Copy with modifications
  copyWith(
    obj: DetailedSafeObject,
    updates: Partial<{
      id: string;
      cls: string;
      parentGroup: string;
      SIGNATURE: string;
      comment: string;
      isValid: boolean;
      process: Process;
      nestedObjects: DetailedSafeObject[];
      isDeleted: boolean;
      applyFunction: ApplyFunction | null;
      originalObjectSIG: string;
      source: string;
      destination: string;
      timestamp: number;
      last_modified: number;
      slot: number;
      pending_objects: DetailedSafeObject[];
      affected_by: DetailedSafeObject[];
      execute: boolean;
      safes: Array<Record<string, any>> | null;
      trustors: Array<Record<string, any>> | null;
      public_key: string | null;
    }>
  ): DetailedSafeObject {
    return {
      cls: updates.cls ?? obj.cls,
      id: updates.id ?? obj.id,
      parentGroup: updates.parentGroup ?? obj.parentGroup,
      SIGNATURE: updates.SIGNATURE ?? obj.SIGNATURE,
      originalObjectSIG: updates.originalObjectSIG ?? obj.originalObjectSIG,
      comment: updates.comment ?? obj.comment,
      applyFunction: updates.applyFunction ?? obj.applyFunction,
      execute: updates.execute ?? obj.execute,
      isValid: updates.isValid ?? obj.isValid,
      process: updates.process ?? obj.process,
      nestedObjects: updates.nestedObjects ?? obj.nestedObjects,
      isDeleted: updates.isDeleted ?? obj.isDeleted,
      source: updates.source ?? obj.source,
      destination: updates.destination ?? obj.destination,
      timestamp: updates.timestamp ?? obj.timestamp,
      last_modified: updates.last_modified ?? obj.last_modified,
      slot: updates.slot ?? obj.slot,
      pending_objects: updates.pending_objects ?? obj.pending_objects,
      affected_by: updates.affected_by ?? obj.affected_by,
      safes: updates.safes ?? obj.safes,
      trustors: updates.trustors ?? obj.trustors,
      public_key: updates.public_key ?? obj.public_key,
    };
  },

  // Extract user trustors
  extractUserTrustors(trustors: Array<Record<string, any>> | null): Array<Record<string, any>> {
    if (!trustors || trustors.length === 0) {
      return [];
    }

    // Group trustors by name
    const groupedByName = trustors.reduce(
      (groups, trustor) => {
        const name = trustor.name || "unknown";
        if (!groups[name]) {
          groups[name] = [];
        }
        groups[name].push(trustor);
        return groups;
      },
      {} as Record<string, Array<Record<string, any>>>
    );

    // Get the last trustor from each group
    return Object.values(groupedByName).map((group) => {
      // Sort by timestamp in descending order to get the most recent
      const sortedGroup = [...group].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      return sortedGroup[0]; // Return the first (most recent) trustor
    });
  },

  isDetailedSafeObject(obj: any): obj is DetailedSafeObject {
    return (
      obj &&
      typeof obj === "object" &&
      "cls" in obj &&
      "id" in obj &&
      "parentGroup" in obj &&
      "SIGNATURE" in obj &&
      "comment" in obj &&
      "isValid" in obj &&
      "process" in obj &&
      "nestedObjects" in obj &&
      "isDeleted" in obj &&
      "applyFunction" in obj &&
      "originalObjectSIG" in obj &&
      "source" in obj &&
      "destination" in obj &&
      "timestamp" in obj &&
      "last_modified" in obj &&
      "slot" in obj &&
      "pending_objects" in obj &&
      "affected_by" in obj
    );
  },
};
